import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SqlServerIcon from './icons/SqlServerIcon';
import OracleIcon from './icons/OracleIcon';
import Db2Icon from './icons/Db2Icon';
import PostgresqlIcon from './icons/PostgresqlIcon';
import MysqlIcon from './icons/MysqlIcon';

const databaseIcons = {
  'sqlserver': SqlServerIcon,
  'oracle': OracleIcon,
  'db2': Db2Icon,
  'postgresql': PostgresqlIcon,
  'mysql': MysqlIcon
};

const DatabaseSupport = () => {
  const { t } = useTranslation();
  const dbData = t('databaseSupport.databases', { returnObjects: true });
  const [activeDb, setActiveDb] = useState(dbData[0]);

  return (
    <section className="bg-dblift-gray-light section" id="database-support">
      <div className="container">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 
              className="text-4xl font-bold text-dblift-blue-dark mb-4"
              dangerouslySetInnerHTML={{ __html: t('databaseSupport.title') }}
            >
            </h2>
            <p className="text-xl text-dblift-gray-dark max-w-3xl mx-auto">
              {t('databaseSupport.description')}
            </p>
          </motion.div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/3">
            <div className="bg-white rounded-xl p-6 shadow-lg h-full">
              <h3 className="text-2xl font-bold text-dblift-blue-dark mb-6">{t('databaseSupport.databasesTitle')}</h3>
              <div className="space-y-4">
                {dbData.map((db) => {
                  const DbIcon = databaseIcons[db.id];
                  return (
                    <button
                      key={db.id}
                      onClick={() => setActiveDb(db)}
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center transition-all ${
                        activeDb.id === db.id
                          ? 'bg-dblift-orange text-white'
                          : 'bg-dblift-gray-light hover:bg-dblift-orange/20'
                      }`}
                    >
                      {DbIcon && <DbIcon className="w-8 h-8 mr-3" />}
                      <div>
                        <span className="font-medium block">{db.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          db.status === 'supported' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {db.status === 'supported' 
                            ? t('databaseSupport.status.supported') 
                            : t('databaseSupport.status.planned')}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:w-2/3">
            <motion.div
              key={activeDb.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl p-8 shadow-lg h-full"
            >
              <div className="flex items-center mb-6">
                {databaseIcons[activeDb.id] && React.createElement(databaseIcons[activeDb.id], { className: "w-16 h-16 mr-4" })}
                <h3 className="text-3xl font-bold text-dblift-blue-dark">{activeDb.name}</h3>
              </div>

              <div className="mb-6">
                <h4 className="text-xl font-semibold mb-4 text-dblift-blue-dark">{t('databaseSupport.features')}</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeDb.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="h-5 w-5 text-dblift-orange mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-4 bg-dblift-gray-light rounded-lg text-sm">
                <p className="mb-2 font-medium">{t('databaseSupport.connectionExample')}</p>
                <code className="block bg-dblift-blue-dark text-white p-3 rounded font-mono">
                  {activeDb.connectionString}
                </code>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DatabaseSupport;